import React, { useState } from "react";
import type { ApplicationRound } from "../../../../core/data/redux/recruitment/types";
import { ROUND_STATUS_COLORS } from "../../constants/recruitment.constants";
import RoundDecisionModal from "./RoundDecisionModal";
import ScheduleInterviewModal from "./ScheduleInterviewModal";

interface Props {
  round: ApplicationRound;
  applicationId: number;
}

const RoundCard: React.FC<Props> = ({ round, applicationId }) => {
  const [showDecision, setShowDecision] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const statusColor = ROUND_STATUS_COLORS[round.round_status] || "#94a3b8";

  const hasInterviews = round.interviews && round.interviews.length > 0;
  const hasFeedback = round.feedback_aggregate && round.feedback_aggregate.total_feedback > 0;

  return (
    <>
      <div
        style={{
          border: `1px solid ${statusColor}30`,
          borderRadius: 12,
          padding: "14px 18px",
          background: `linear-gradient(135deg, ${statusColor}06, transparent)`,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          cursor: round.is_terminal ? "default" : "pointer",
        }}
        onMouseEnter={(e) => {
          if (!round.is_terminal) {
            e.currentTarget.style.boxShadow = `0 2px 12px ${statusColor}18`;
            e.currentTarget.style.borderColor = `${statusColor}60`;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = `${statusColor}30`;
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: statusColor,
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {round.stage_order}
            </span>
            <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
              {round.round_name}
            </span>
            {round.is_mandatory && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#E74C3C",
                  background: "#E74C3C14",
                  padding: "1px 6px",
                  borderRadius: 4,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Required
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Status badge */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                fontWeight: 600,
                color: statusColor,
                background: `${statusColor}14`,
                padding: "3px 8px",
                borderRadius: 999,
                border: `1px solid ${statusColor}30`,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: statusColor,
                }}
              />
              {round.round_status_text}
            </span>

            {/* Schedule button */}
            {!round.is_terminal && round.round_status !== "completed" && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowSchedule(true); }}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#10b981",
                  background: "#10b9810a",
                  border: "1px solid #10b98130",
                  borderRadius: 6,
                  padding: "3px 10px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#10b98118";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#10b9810a";
                }}
              >
                <i className="ti ti-calendar-plus me-1" style={{ fontSize: 11 }} />
                Schedule
              </button>
            )}

            {/* Decision button */}
            {!round.is_terminal && (
              <button
                onClick={() => setShowDecision(true)}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#6366f1",
                  background: "#6366f10a",
                  border: "1px solid #6366f130",
                  borderRadius: 6,
                  padding: "3px 10px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#6366f118";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#6366f10a";
                }}
              >
                <i className="ti ti-gavel me-1" style={{ fontSize: 11 }} />
                Decide
              </button>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
          <span>
            <i className="ti ti-clock me-1" style={{ fontSize: 12 }} />
            {round.duration_minutes}min
          </span>
          <span>
            <i className="ti ti-puzzle me-1" style={{ fontSize: 12 }} />
            {round.round_type_text}
          </span>
          {round.score && (
            <span>
              <i className="ti ti-star me-1" style={{ fontSize: 12 }} />
              Score: {round.score}
            </span>
          )}
          {hasFeedback && (
            <span>
              <i className="ti ti-message-circle me-1" style={{ fontSize: 12 }} />
              {round.feedback_aggregate.total_feedback} feedback
              {round.feedback_aggregate.avg_rating && (
                <> · {round.feedback_aggregate.avg_rating}★</>
              )}
            </span>
          )}
        </div>

        {/* Interviews sub-list */}
        {hasInterviews && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Interviews
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
              {round.interviews.map((iv) => (
                <div
                  key={iv.interview_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 11,
                    color: "#475569",
                    padding: "4px 8px",
                    borderRadius: 6,
                    background: "#f8fafc",
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: ROUND_STATUS_COLORS[iv.status] || "#94a3b8",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>
                    {new Date(iv.scheduled_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <span style={{ color: "#94a3b8" }}>·</span>
                  <span>{iv.duration_minutes}min</span>
                  <span style={{ color: "#94a3b8" }}>·</span>
                  <span style={{ textTransform: "capitalize" }}>{iv.status}</span>
                  {iv.meeting_link && (
                    <a
                      href={iv.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#6366f1", fontSize: 10, marginLeft: "auto" }}
                    >
                      <i className="ti ti-external-link" style={{ fontSize: 11 }} /> Join
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Override badge */}
        {round.result_override && (
          <div
            style={{
              marginTop: 8,
              fontSize: 10,
              color: "#F39C12",
              background: "#F39C1214",
              padding: "3px 8px",
              borderRadius: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <i className="ti ti-alert-triangle" style={{ fontSize: 11 }} />
            Result overridden by HR
          </div>
        )}
      </div>

      {showDecision && (
        <RoundDecisionModal
          round={round}
          applicationId={applicationId}
          onClose={() => setShowDecision(false)}
        />
      )}

      {showSchedule && (
        <ScheduleInterviewModal
          round={round}
          applicationId={applicationId}
          open={showSchedule}
          onClose={() => setShowSchedule(false)}
          onSuccess={() => setShowSchedule(false)}
        />
      )}
    </>
  );
};

export default RoundCard;
